import type { CookieConsentConfig, Translation } from "vanilla-cookieconsent";

import type { Locale } from "@/lib/i18n";

export const MARKETING_CATEGORY = "marketing";

const en: Translation = {
  consentModal: {
    label: "Cookie consent",
    description:
      'One cookie with your matcha? It helps us find more people who\'d love Matchamind. Essential cookies are always on, the rest is your call. <a href="/privacy-policy">See our privacy policy</a>',
    acceptAllBtn: "Accept all",
    acceptNecessaryBtn: "Essential only",
    showPreferencesBtn: "Customize",
  },
  preferencesModal: {
    title: "How Matchamind uses cookies",
    acceptAllBtn: "Accept all",
    acceptNecessaryBtn: "Reject all",
    savePreferencesBtn: "Save preferences",
    closeIconLabel: "Close",
    sections: [
      {
        title: "Strictly necessary",
        description: "Needed for the site to work, such as remembering this choice. Always on.",
        linkedCategory: "necessary",
      },
      {
        title: "Marketing (Meta pixel)",
        description:
          "Meta (Facebook) pixel: measures our ads and lets us reach visitors of this site on Meta platforms. Sets the _fbp and _fbc cookies for up to 90 days.",
        linkedCategory: MARKETING_CATEGORY,
      },
      {
        title: "More information",
        description:
          'See our <a href="/privacy-policy">privacy policy</a> or <a href="/contact">contact us</a>.',
      },
    ],
  },
};

const fr: Translation = {
  consentModal: {
    label: "Consentement aux cookies",
    description:
      "Un cookie avec votre matcha ? Il nous aide à toucher d'autres personnes qui aimeraient Matchamind. Les cookies essentiels sont toujours actifs, pour le reste c'est vous qui voyez. <a href=\"/privacy-policy?lang=fr\">Voir notre politique de confidentialité</a>",
    acceptAllBtn: "Tout accepter",
    acceptNecessaryBtn: "Essentiels uniquement",
    showPreferencesBtn: "Personnaliser",
  },
  preferencesModal: {
    title: "Comment Matchamind utilise les cookies",
    acceptAllBtn: "Tout accepter",
    acceptNecessaryBtn: "Tout refuser",
    savePreferencesBtn: "Enregistrer",
    closeIconLabel: "Fermer",
    sections: [
      {
        title: "Strictement nécessaires",
        description:
          "Indispensables au fonctionnement du site, par exemple pour mémoriser ce choix. Toujours actifs.",
        linkedCategory: "necessary",
      },
      {
        title: "Marketing (pixel Meta)",
        description:
          "Pixel Meta (Facebook) : mesure nos publicités et nous permet de toucher les visiteurs de ce site sur les plateformes Meta. Dépose les cookies _fbp et _fbc pour 90 jours maximum.",
        linkedCategory: MARKETING_CATEGORY,
      },
      {
        title: "Plus d'informations",
        description:
          'Consultez notre <a href="/privacy-policy?lang=fr">politique de confidentialité</a> ou <a href="/contact?lang=fr">contactez-nous</a>.',
      },
    ],
  },
};

/** Opt-in consent config: nothing outside "necessary" runs until the visitor accepts. */
export function cookieConsentConfig(
  locale: Locale,
  onMarketingConsent: () => void,
): CookieConsentConfig {
  return {
    guiOptions: {
      // Compact corner card (GitHub / Vercel style): reads as a notice, keeps the
      // hero and its CTAs clear. Filled "Accept all" + outlined "Essential only".
      consentModal: { layout: "box inline", position: "bottom right", equalWeightButtons: false },
      preferencesModal: { layout: "box", equalWeightButtons: true },
    },
    categories: {
      necessary: { enabled: true, readOnly: true },
      [MARKETING_CATEGORY]: {
        // Withdrawing consent must stop an already-loaded pixel: clear its
        // cookies and reload so the page starts clean.
        autoClear: { cookies: [{ name: /^_fb/ }], reloadPage: true },
      },
    },
    language: { default: locale, translations: { en, fr } },
    onConsent: ({ cookie }) => {
      if (cookie.categories.includes(MARKETING_CATEGORY)) onMarketingConsent();
    },
    onChange: ({ cookie }) => {
      if (cookie.categories.includes(MARKETING_CATEGORY)) onMarketingConsent();
    },
  };
}
